import { type IconType } from "../../icon"
import Image from "next/image"
import Link from "next/link"
import React from "react"

export type FeatureCardVariant =
  | "large-image" // card with large image and caption
  | "large-image-contain" // card with large contained and padded image and caption

export type FeatureCardProps = {
  title?: string | JSX.Element
  body?: string | string[] | JSX.Element
  captionBg?: string // defaults to grey
  titleClass?: string
  bodyClass?: string
  cardClass?: string

  cardRing?: boolean

  image?: string | IconType | JSX.Element
  imageBg?: string // only used in contain; defaults to grey
  imageClass?: string
  imageContainClass?: string
  imageSizes?: string // if we want to add an <Image /> sizes prop
  imageQuality?: number // in case images come out crunchy
  // only used for non-contain, which requires a height to be set for image.  width is based on grid width
  imageHeight?: string
  // only used for contain with images; if we want the white circle background for ISO badges.  string is the class for the div
  imageIsoCircle?: string
  alt?: string // alt for the image, if it's an image
  extraBgClass?: string // for filter over image

  imageOnHover?: "opacity" | "scale" | "none" // defaults to opacity

  // should only have one of these
  href?: string
  onClick?: () => void

  // only for href
  newTab?: boolean

  // if we want a cypress id
  dataCy?: string
}

const cardClass = `rounded-xl overflow-hidden group h-full w-full relative`

export const FeatureCard = (props: FeatureCardProps & { variant: FeatureCardVariant }) => {
  const imageBg = props.imageBg ?? `bg-white/30 group-hover:bg-white/40`
  const captionBg = props.captionBg ?? `bg-white/30 group-hover:bg-white/40`

  // these get overwritten by titleClass
  const titleClass = `font-bold text-white text-base md:text-lg`
  // these need to be on the class itself
  const undertitleClass = `transition-colors duration-200 ${props.titleClass ?? ""}`

  const hasCaption = props.title || props.body

  const imageAnimation =
    props.imageOnHover === "none" ? "" : props.imageOnHover === "scale" ? "scale-100 group-hover:scale-110 transition-transform" : "opacity-90 group-hover:opacity-100 transition-opacity" // defaults to opacity animation

  switch (props.variant) {
    case "large-image":
      return (
        <div data-cy={props.dataCy} className={`${cardClass} ${props.cardClass ?? ""} relative`}>
          {props.href && <Link href={props.href} className="absolute inset-0 z-10" {...(props.newTab ? { target: "_blank", rel: "noreferrer noopener" } : {})} />}
          {props.onClick && <button onClick={props.onClick} className="absolute inset-0 z-10" />}
          {
            typeof props.image === "string" ? (
              <div className={`relative flex-grow ${props.imageHeight ?? "h-[6rem] md:h-[8rem] xl:h-[8rem]"}`}>
                <Image
                  src={props.image}
                  alt={props.alt ?? ""}
                  className={`${props.imageClass ?? ""} ${props.imageContainClass ?? "object-cover"} h-full ${imageAnimation} duration-200`}
                  fill
                  sizes={props.imageSizes}
                  quality={props.imageQuality}
                />
                <div className={`${props.extraBgClass ?? ""} relative z-10 h-full w-full`} />
              </div>
            ) : (
              <div />
            ) // SVG logos shouldn't be 'object-cover'
          }
          {hasCaption && (
            <div className={`${captionBg} h-full p-3 pb-6 transition-colors duration-200 lg:p-4`}>
              <div className={titleClass}>
                <h3 className={undertitleClass}>{props.title}</h3>
              </div>
              <div className={`${props.bodyClass ?? "text-gray-300"} text-resp-base mt-2`}>{props.body}</div>
            </div>
          )}
          {props.cardRing && <div className="group-hover:purpleblue/30 pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-white/20" />}
        </div>
      )
    case "large-image-contain":
      return (
        <div data-cy={props.dataCy} className={`${cardClass} ${props.cardClass ?? ""} relative`}>
          {props.href && <Link href={props.href} className="absolute inset-0 z-10" {...(props.newTab ? { target: "_blank", rel: "noreferrer noopener" } : {})} />}
          {props.onClick && <button onClick={props.onClick} className="absolute inset-0 z-20" />}
          <div className={`relative ${imageBg} transition-colors duration-200`}>
            {typeof props.image === "string" ? (
              <div className={`relative z-0 mx-auto ${props.imageHeight ?? "h-[6rem] md:h-[8rem] xl:h-[8rem]"}`}>
                <Image src={props.image} alt={props.alt ?? ""} className={`${props.imageClass ?? "px-4 py-4 lg:px-6"} ${props.imageContainClass ?? "object-contain"}`} fill sizes={props.imageSizes} />
                {props.imageIsoCircle && (
                  <div className={`absolute inset-x-1 -z-10 mx-auto aspect-square translate-y-[1px] rounded-full bg-white ${props.imageIsoCircle}`}>
                    <div className={``} />
                  </div>
                )}
                <div className={`${props.extraBgClass ?? ""} relative z-10 h-full w-full`} />
              </div>
            ) : typeof props.image === "function" ? (
              <div className={``}>
                <props.image className={`${props.imageClass ?? "h-[6rem] md:h-[8rem] xl:h-[8rem] px-4 py-4 lg:px-6"} mx-auto`} />
                <div className={`${props.extraBgClass ?? ""} relative z-10 h-full w-full`} />
              </div>
            ) : (
              <div className="">
                {props.image}
                <div className={`${props.extraBgClass ?? ""} relative z-10 h-full w-full`} />
              </div>
            )}
          </div>
          {hasCaption && (
            <div className={`${captionBg} h-full p-3 pb-6 transition-colors duration-200 lg:p-4 lg:pb-8`}>
              <div className={titleClass}>
                <h3 className={undertitleClass}>{props.title}</h3>
              </div>
              <div className={`${props.bodyClass ?? "text-gray-300"} mt-0.5 text-sm md:text-base/5`}>{props.body}</div>
            </div>
          )}
          {props.cardRing && <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-white/20" />}
        </div>
      )
  }
}
export default FeatureCard
